import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TOKENS, WA_LINK } from '../tokens';
import { ChapterTag } from './Details';

/*
  FORM SETUP — where do submissions go?
  ──────────────────────────────────────
  This form uses Formspree (free service).
  To receive form submissions by email:
  1. Go to formspree.io → sign up free
  2. Create a new form → copy your Form ID
  3. Replace YOUR_FORM_ID below with it
  4. Rebuild and redeploy
  You'll get an email every time someone fills the form.
*/
const FORMSPREE_ID = 'xqewglov'; // e.g. "xdoqvgkj"

function InputField({ label, type = 'text', placeholder, name, as = 'input', required }) {
  const [focused, setFocused] = useState(false);
  const Tag = as;
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: 'block', fontSize: '0.58rem', fontWeight: 500,
        letterSpacing: '0.18em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.3)', marginBottom: 7,
      }}>{label}</label>
      <Tag
        type={type}
        name={name}
        placeholder={placeholder}
        required={required}
        rows={as === 'textarea' ? 3 : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          background: focused ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${focused ? 'rgba(200,169,106,0.4)' : 'rgba(255,255,255,0.08)'}`,
          borderRadius: 10, color: '#fff',
          fontFamily: "'Outfit', sans-serif",
          fontSize: '0.84rem', fontWeight: 300,
          padding: '13px 16px', outline: 'none',
          transition: 'border-color 0.2s, background 0.2s',
          resize: 'none', cursor: 'none',
          appearance: 'none',
        }}
      />
    </div>
  );
}

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true);

    // If Formspree is configured, submit there
    if (FORMSPREE_ID !== 'YOUR_FORM_ID') {
      try {
        const formData = new FormData(e.target);
        await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });
      } catch (_) { /* silent fail — still show success */ }
    }

    setSending(false);
    setSubmitted(true);
  }

  return (
    <section id="contact" style={{
      background: '#050A1A',
      padding: 'clamp(80px,10vw,120px) clamp(20px,4vw,48px)',
      borderTop: '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72,
        maxWidth: 1000, margin: '0 auto', alignItems: 'start',
      }} className="contact-grid">

        {/* Left — contact info */}
        <motion.div
          initial={{ opacity: 0, x: -44 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <ChapterTag text="Final Step · Get in Touch" />
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(1.8rem, 3vw, 2.8rem)',
            fontWeight: 300, lineHeight: 1.2,
            color: '#fff', marginBottom: 14,
          }}>
            Serious enquiries<br />
            <em style={{ fontStyle: 'italic', color: TOKENS.GOLD }}>only, please.</em>
          </h2>
          <p style={{ fontSize: '0.85rem', fontWeight: 300, lineHeight: 1.82, color: TOKENS.MUTED, marginBottom: 36 }}>
            We respond to every genuine inquiry within 2 hours. Site visits can be arranged at your convenience, any day of the week.
          </p>

          {/* WhatsApp — primary action */}
          <a
            href={WA_LINK()}
            target="_blank" rel="noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              padding: '16px 28px',
              background: 'rgba(37,211,102,0.1)',
              border: '1px solid rgba(37,211,102,0.22)',
              borderRadius: 980, color: '#4dda82',
              fontFamily: "'Outfit', sans-serif",
              fontSize: '0.84rem', fontWeight: 500,
              textDecoration: 'none',
              transition: 'all 0.3s', cursor: 'none',
              marginBottom: 32,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.18)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.1)'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Message on WhatsApp · Instant Response
          </a>

        </motion.div>

        {/* Right — form */}
        <motion.div
          initial={{ opacity: 0, x: 44 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          style={{
            background: '#0A1030',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 20, padding: 'clamp(28px,4vw,44px)',
          }}
        >
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: '1.5rem', fontWeight: 300, color: '#fff', marginBottom: 5,
          }}>
            Request Information
          </div>
          <div style={{ fontSize: '0.72rem', color: TOKENS.MUTED, marginBottom: 28 }}>
            We'll call you back within 2 hours.
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 0 }}
                   className="form-row">
                <InputField label="Full Name" name="name" placeholder="Your name" required />
                <InputField label="Phone" type="tel" name="phone" placeholder="+91 98765 43210" required />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'block', fontSize: '0.58rem', fontWeight: 500,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.3)', marginBottom: 7,
                }}>
                  I Am Interested As
                </label>
                <select name="interest" style={{
                  width: '100%', background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
                  color: '#fff', fontFamily: "'Outfit', sans-serif",
                  fontSize: '0.84rem', fontWeight: 300, padding: '13px 16px',
                  outline: 'none', appearance: 'none', cursor: 'none',
                }}>
                  <option value="">Select your profile</option>
                  <option>Investor / Portfolio Buyer</option>
                  <option>Business Owner (Warehouse / Manufacturing)</option>
                  <option>Commercial Property Buyer</option>
                  <option>Other</option>
                </select>
              </div>

              <InputField label="Message (Optional)" as="textarea" name="message"
                placeholder="Preferred visit time, specific questions…" />

              <button
                type="submit"
                disabled={sending}
                style={{
                  width: '100%', marginTop: 10, padding: 15,
                  background: sending ? '#8B6A3A' : TOKENS.GOLD,
                  color: '#000',
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.06em',
                  border: 'none', borderRadius: 980, cursor: sending ? 'wait' : 'none',
                  transition: 'all 0.3s',
                  opacity: sending ? 0.7 : 1,
                }}
                onMouseEnter={e => { if (!sending) { e.currentTarget.style.background = TOKENS.GOLD_LIGHT; e.currentTarget.style.transform = 'scale(1.015)'; } }}
                onMouseLeave={e => { e.currentTarget.style.background = sending ? '#8B6A3A' : TOKENS.GOLD; e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {sending ? 'Sending…' : 'Request a Site Visit →'}
              </button>
              <p style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.18)', textAlign: 'center', marginTop: 12 }}>
                Your details are confidential and never shared with third parties.
              </p>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              style={{ textAlign: 'center', padding: '32px 0' }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'rgba(200,169,106,0.12)',
                  border: '1px solid rgba(200,169,106,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: '1.6rem',
                }}
              >
                ✓
              </motion.div>
              <p style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '1.4rem', fontWeight: 300, color: '#fff', marginBottom: 8,
              }}>
                Request Received
              </p>
              <p style={{ fontSize: '0.82rem', color: TOKENS.MUTED, lineHeight: 1.7 }}>
                We'll call you within 2 hours.<br />
                Or message us directly on WhatsApp for an instant reply.
              </p>
              <a
                href={WA_LINK()}
                target="_blank" rel="noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  marginTop: 20, padding: '11px 22px',
                  background: '#25D366', color: '#fff',
                  borderRadius: 980, fontSize: '0.76rem', fontWeight: 600,
                  textDecoration: 'none', cursor: 'none',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Message on WhatsApp
              </a>
            </motion.div>
          )}
        </motion.div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
          .form-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
